import React, { useState } from 'react';
import { BotIcon, TrendingUpIcon, MegaphoneIcon, BarChart3Icon, MessageSquareIcon, WorkflowIcon, ZapIcon } from 'lucide-react';
import { DemoModal } from './DemoModal';
export function Services() {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const consultingServices = [{
    icon: <WorkflowIcon size={28} className="text-blue-600" data-id="element-277" />,
    title: 'Process & Workflow Automation',
    description: 'Reclaim 2.5+ hours per day lost to manual, rule-based work. We map your operations and deploy AI agents that execute multi-step workflows 24/7 — eliminating handoffs and reducing costs.',
    useCases: ['Workflow Automation', 'Document AI', 'Contract Management', 'Agentic AI', 'Multi-step Automation']
  }, {
    icon: <TrendingUpIcon size={28} className="text-blue-600" data-id="element-278" />,
    title: 'Sales & Revenue Acceleration',
    description: 'Build 2–3× more pipeline without hiring more SDRs. AI-driven prospecting, personalization, and follow-up that gives every prospect a tailored touch and delivers predictable revenue.',
    useCases: ['SDR Automation', 'CRM Optimization', 'AI-Enhanced CRM', 'Pipeline Forecasting']
  }, {
    icon: <MegaphoneIcon size={28} className="text-blue-600" data-id="element-279" />,
    title: 'Marketing Intelligence & Automation',
    description: 'Multiply content output 3–5× and automate campaigns end-to-end — from creation to optimization to lead nurturing — lowering cost per lead without adding headcount.',
    useCases: ['Content Generation', 'Campaign Automation', 'Personalization at Scale', 'Performance Optimization']
  }, {
    icon: <MessageSquareIcon size={28} className="text-blue-600" data-id="element-280" />,
    title: 'Customer Engagement AI',
    description: 'Resolve 60–80% of inbound support volume around the clock. AI chatbots and voice agents that handle support, qualify leads, and guide onboarding — so your team focuses on high-value conversations.',
    useCases: ['AI Chatbots', 'Voice Agents', 'Support Automation', 'AI-Driven Onboarding']
  }, {
    icon: <BarChart3Icon size={28} className="text-blue-600" data-id="element-281" />,
    title: 'Agentic AI & Multi-step Automation',
    description: 'Autonomous AI agents that plan, reason, and execute complex workflows end-to-end — from data gathering to decision-making — with no manual handoffs or constant oversight.',
    useCases: ['Agentic Workflows', 'Multi-step Automation', 'Autonomous Agents', 'AI Orchestration']
  }, {
    icon: <BotIcon size={28} className="text-blue-600" data-id="element-282" />,
    title: 'AI Stack Integration',
    description: 'Connect AI seamlessly to your CRM, ERP, and marketing platforms. We handle the technical complexity so your team just sees results — no disruption, no learning curve.',
    useCases: ['CRM Integration', 'ERP Connectivity', 'Multi-system Automation', 'API Orchestration']
  }];
  return <section id="services" className="py-20 bg-gray-50" data-id="element-283">
      <div className="container mx-auto px-4" data-id="element-284">
        <div className="text-center mb-16" data-id="element-285">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-286">
            AI Services Built for Growth
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto mb-6 rounded-full" data-id="element-287"></div>
          <p className="max-w-2xl mx-auto text-gray-600 text-lg" data-id="element-288">
            We focus on streamlining operations and accelerating revenue for
            growing companies through strategic AI implementation.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20" data-id="element-289">
          {consultingServices.map((service, index) => <div key={index} className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm flex flex-col" data-id="element-290">
              <div className="mb-5 p-3 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg w-fit border border-purple-100/50" data-id="element-291">
                {service.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3" data-id="element-292">
                {service.title}
              </h3>
              <p className="text-gray-600 leading-relaxed text-sm mb-6 flex-grow" data-id="element-293">
                {service.description}
              </p>

              {index === 1 && <button onClick={() => setIsDemoModalOpen(true)} className="mb-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white text-sm font-semibold rounded-lg transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5" data-id="element-294">
                  <ZapIcon size={16} data-id="element-295" />
                  Live Demo
                </button>}

              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100" data-id="element-296">
                {service.useCases.map((tag, tagIndex) => <span key={tagIndex} className="text-xs font-medium px-2.5 py-1 rounded-full border bg-blue-50 text-blue-700 border-blue-100" data-id="element-297">
                    {tag}
                  </span>)}
              </div>
            </div>)}
        </div>

        <div className="bg-white p-8 md:p-12 rounded-2xl border border-gray-200 shadow-sm" data-id="element-298">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-299">
            <div className="md:w-1/2" data-id="element-300">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-301">
                Our Consulting Approach
              </h3>
              <p className="text-gray-600 mb-6 text-lg leading-relaxed" data-id="element-302">
                We don't just implement AI — we engage deeply with your team,
                map your highest-impact opportunities, and deliver measurable
                results within 90 days.
              </p>
              <ul className="space-y-3 mb-8" data-id="element-303">
                <li className="flex items-center gap-3 text-gray-700" data-id="element-304">
                  <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full shrink-0" data-id="element-305"></div>
                  <span data-id="element-306">Strategic roadmap & opportunity mapping</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700" data-id="element-307">
                  <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full shrink-0" data-id="element-308"></div>
                  <span data-id="element-309">Custom implementation & integration</span>
                </li>
                <li className="flex items-center gap-3 text-gray-700" data-id="element-310">
                  <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full shrink-0" data-id="element-311"></div>
                  <span data-id="element-312">Team training & change management</span>
                </li>
              </ul>
              <a href="#contact" className="text-blue-600 font-semibold hover:text-blue-700 inline-flex items-center gap-2" data-id="element-313">
                Discuss your needs <span aria-hidden="true" data-id="element-314">&rarr;</span>
              </a>
            </div>
            <div className="md:w-1/2" data-id="element-315">
              <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Strategic consulting session" className="rounded-xl shadow-lg w-full object-cover h-64 md:h-auto" data-id="element-316" />
            </div>
          </div>
        </div>
      </div>

      <DemoModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} data-id="element-317" />
    </section>;
}